if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.initImageZoom();
      }

      hide() {
        this.resetImageZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }


      // Only Shopify's image modal owns these gestures; Teeinblue stays untouched.
      initImageZoom() {
        this.zoomSurface = this.querySelector('[role="document"]');
        if (!this.zoomSurface) return;
        this.zoomPointers = new Map();
        this.zoomScale = 1;
        this.zoomX = this.zoomY = 0;
        this.zoomSurface.addEventListener('pointerdown', (event) => {
          if (!this.zoomImage || (event.pointerType === 'mouse' && event.button !== 0)) return;
          event.preventDefault();
          this.zoomImage.style.transition = 'none';
          this.zoomSurface.setPointerCapture(event.pointerId);
          this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (this.zoomPointers.size === 1) {
            this.zoomTapStart = { x: event.clientX, y: event.clientY, time: Date.now() };
            this.zoomMoved = false;
            this.zoomMultiTouch = false;
          } else {
            this.zoomMultiTouch = true;
            this.zoomLastTap = null;
          }
          this.startZoomGesture();
        });
        this.zoomSurface.addEventListener('pointermove', (event) => {
          if (!this.zoomImage || !this.zoomPointers.has(event.pointerId)) return;
          event.preventDefault();
          this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (Math.hypot(event.clientX - this.zoomTapStart.x, event.clientY - this.zoomTapStart.y) > 8) this.zoomMoved = true;
          const points = Array.from(this.zoomPointers.values());
          const start = this.zoomGesture;
          if (points.length >= 2) {
            const mid = this.zoomMidpoint(points);
            const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
            const scale = Math.min(4, Math.max(1, start.scale * distance / Math.max(1, start.distance)));
            const ratio = scale / start.scale;
            this.zoomX = mid.x - (start.mid.x - start.x) * ratio;
            this.zoomY = mid.y - (start.mid.y - start.y) * ratio;
            this.zoomScale = scale;
          } else if (this.zoomScale > 1) {
            this.zoomX = start.x + points[0].x - start.point.x;
            this.zoomY = start.y + points[0].y - start.point.y;
          }
          this.renderImageZoom();
        });
        const end = (event) => {
          if (!this.zoomImage || !this.zoomPointers.has(event.pointerId)) return;
          // Do not let ModalDialog's mouse pointerup handler close the image.
          event.stopPropagation();
          this.zoomPointers.delete(event.pointerId);
          if (this.zoomSurface.hasPointerCapture(event.pointerId)) this.zoomSurface.releasePointerCapture(event.pointerId);
          if (this.zoomPointers.size) {
            this.startZoomGesture();
          } else if (event.type === 'pointerup' && !this.zoomMoved && !this.zoomMultiTouch && Date.now() - this.zoomTapStart.time < 300) {
            const now = Date.now();
            if (this.zoomLastTap && now - this.zoomLastTap.time < 320 && Math.hypot(event.clientX - this.zoomLastTap.x, event.clientY - this.zoomLastTap.y) < 30) {
              this.toggleImageZoom(event.clientX, event.clientY);
              this.zoomLastTap = null;
            } else {
              this.zoomLastTap = { x: event.clientX, y: event.clientY, time: now };
            }
          } else {
            this.zoomLastTap = null;
          }
        };
        this.zoomSurface.addEventListener('pointerup', end);
        this.zoomSurface.addEventListener('pointercancel', end);
        this.zoomSurface.addEventListener('lostpointercapture', (event) => {
          if (!this.zoomPointers.has(event.pointerId)) return;
          this.zoomPointers.delete(event.pointerId);
          this.zoomLastTap = null;
          if (this.zoomPointers.size) this.startZoomGesture();
        });
        this.zoomSurface.addEventListener('click', (event) => {
          if (!this.zoomImage) return;
          event.preventDefault();
          event.stopPropagation();
        });
        this.zoomSurface.addEventListener('dragstart', (event) => {
          if (this.zoomImage) event.preventDefault();
        });
        // Safari must not apply a second, page-level zoom to the same gesture.
        ['gesturestart', 'gesturechange', 'gestureend'].forEach((name) => {
          this.zoomSurface.addEventListener(name, (event) => {
            if (this.zoomImage) event.preventDefault();
          }, { passive: false });
        });
      }

      zoomMidpoint(points) {
        const rect = this.zoomSurface.getBoundingClientRect();
        return {
          x: (points[0].x + points[1].x) / 2 - rect.left - rect.width / 2,
          y: (points[0].y + points[1].y) / 2 - rect.top - rect.height / 2
        };
      }

      startZoomGesture() {
        const points = Array.from(this.zoomPointers.values());
        this.zoomGesture = {
          scale: this.zoomScale, x: this.zoomX, y: this.zoomY, point: points[0],
          mid: points.length >= 2 ? this.zoomMidpoint(points) : null,
          distance: points.length >= 2 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0
        };
      }

      renderImageZoom() {
        if (!this.zoomImage) return;
        const maxX = Math.max(0, (this.zoomImage.clientWidth * this.zoomScale - this.zoomSurface.clientWidth) / 2);
        const maxY = Math.max(0, (this.zoomImage.clientHeight * this.zoomScale - this.zoomSurface.clientHeight) / 2);
        this.zoomX = Math.max(-maxX, Math.min(maxX, this.zoomX));
        this.zoomY = Math.max(-maxY, Math.min(maxY, this.zoomY));
        if (this.zoomFrame) return;
        this.zoomFrame = requestAnimationFrame(() => {
          this.zoomFrame = null;
          if (this.zoomImage) this.zoomImage.style.transform = 'translate3d(' + this.zoomX + 'px, ' + this.zoomY + 'px, 0) scale(' + this.zoomScale + ')';
        });
      }

      toggleImageZoom(clientX, clientY) {
        const rect = this.zoomSurface.getBoundingClientRect();
        const scale = this.zoomScale > 1 ? 1 : 2.5;
        const ratio = scale / this.zoomScale;
        const x = clientX - rect.left - rect.width / 2;
        const y = clientY - rect.top - rect.height / 2;
        this.zoomX = x - (x - this.zoomX) * ratio;
        this.zoomY = y - (y - this.zoomY) * ratio;
        this.zoomScale = scale;
        this.zoomImage.style.transition = 'transform 160ms ease-out';
        this.renderImageZoom();
      }

      resetImageZoom() {
        if (this.zoomFrame) cancelAnimationFrame(this.zoomFrame);
        this.zoomFrame = null;
        if (this.zoomImage) {
          this.zoomImage.style.removeProperty('transform');
          this.zoomImage.style.removeProperty('transition');
        }
        this.zoomImage = null;
        if (this.zoomPointers) {
          this.zoomPointers.forEach((point, id) => {
            if (this.zoomSurface.hasPointerCapture(id)) this.zoomSurface.releasePointerCapture(id);
          });
          this.zoomPointers.clear();
        }
        this.zoomLastTap = null;
        this.zoomScale = 1;
        this.zoomX = this.zoomY = 0;
        this.classList.remove('df-touch-zoom');
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        this.resetImageZoom();
        const useTouchZoom = activeMedia.tagName === 'IMG' && window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
        if (useTouchZoom) {
          this.zoomImage = activeMedia;
          this.classList.add('df-touch-zoom');
        }
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        if (!useTouchZoom) activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = useTouchZoom ? 0 : (activeMedia.width - container.clientWidth) / 2;
        if (useTouchZoom) container.scrollTop = 0;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
